// ---- Tiered prices (NEW) ----
// Cost of the Nth station the player owns (0-indexed: stations.length BEFORE purchase).
// 1st=$20k, 2nd=$100k, 3rd=$250k, 4th=$400k, 5th+ = $500k.
export const STATION_PRICES = [20000, 100000, 250000, 400000];
export const STATION_PRICE_DEFAULT = 500000;
export const stationCost = (ownedCount) =>
  STATION_PRICES[ownedCount] ?? STATION_PRICE_DEFAULT;

// Cost of the next bay purchased on a station, based on how many bays already exist.
// Stations start with 2 free bays; the 1st purchase (bringing total to 3) is $10k.
// 1st=$10k, 2nd=$20k, 3rd=$40k, 4th=$80k, 5th+ = $120k.
export const BAY_PRICES = [10000, 20000, 40000, 80000];
export const BAY_PRICE_DEFAULT = 120000;
const STATION_FREE_BAYS = 2;
export const bayCost = (currentBays) => {
  const purchaseIndex = Math.max(0, currentBays - STATION_FREE_BAYS);
  return BAY_PRICES[purchaseIndex] ?? BAY_PRICE_DEFAULT;
};

// Legacy single-number cost (still used by HUD button label as the starter price).
export const STATION_COST = STATION_PRICES[0];
export const STARTING_MONEY = 50000;
export const BAY_COST = BAY_PRICES[0]; // legacy alias

// ---- Gem conversion ---- (1 gem = $500 of purchase value, rounded to nearest gem)
export const GEM_TO_MONEY = 500;
export const gemCost = (moneyAmount) =>
  Math.max(1, Math.round((moneyAmount || 0) / GEM_TO_MONEY));
// Gem cost to instantly finish a call without on-scene units. Scales at
// 1 gem per $500 of reward (the 1 gem = $500 economy rate) with a 3-gem
// floor for low-reward calls. Server mirrors this in routes/game.py.
export const quickFinishGems = (reward) => Math.max(3, Math.ceil((reward || 0) / GEM_TO_MONEY));

// ---- Unit types ----
// HEMS = Helicopter Emergency Medical Services (air ambulance). Treated as
// an EMS-class transport unit alongside Medic — both can assess patients
// and transport them to hospitals. Future work: Airport Base facility.
export const UNIT_TYPES = [
  "Engine", "Ladder", "Battalion Chief", "Tanker", "Brush Truck", "Rescue", "Hazmat", "Medic", "HEMS",
  // Police
  "Patrol Car", "Deputy", "K9 Unit", "Sheriff", "Police Chief",
];
// Fire/EMS units are housed at Fire Stations; Police units at Police
// Stations. Enforced by the buy_apparatus endpoint too.
export const FIRE_UNIT_TYPES = ["Engine", "Ladder", "Battalion Chief", "Tanker", "Brush Truck", "Rescue", "Hazmat", "Medic", "HEMS"];
export const POLICE_UNIT_TYPES = ["Patrol Car", "Deputy", "K9 Unit", "Sheriff", "Police Chief"];
// Sheriff + Police Chief BOTH count as "Supervisor" when a call's
// required_units asks for the abstract Supervisor role.
export const SUPERVISOR_UNITS = ["Sheriff", "Police Chief"];
// Only these can transport an arrestee to a jail cell.
export const ARREST_TRANSPORT_UNITS = ["Patrol Car", "Deputy"];

// Unit types that can assess patients on scene AND transport them to a
// hospital. Add new types here when they become EMS-capable.
export const TRANSPORT_UNIT_TYPES = ["Medic", "HEMS"];
export const isTransportUnit = (type) => TRANSPORT_UNIT_TYPES.includes(type);

export const APPARATUS_COST = {
  "Engine": 10000,
  "Ladder": 25000,
  "Battalion Chief": 7000,
  "Tanker": 15000,
  "Brush Truck": 7000,
  "Rescue": 30000,
  "Hazmat": 30000,
  "Medic": 15000,
  "HEMS": 70000,
  "Patrol Car": 40000,
  "Deputy": 50000,
  "K9 Unit": 65000,
  "Sheriff": 80000,
  "Police Chief": 100000,
};

export const UNIT_COLOR = {
  "Engine": "#FF3B30",
  "Ladder": "#0A84FF",
  "Battalion Chief": "#BF5AF2",
  "Tanker": "#0EC2C2",
  "Brush Truck": "#30D158",
  "Rescue": "#FFD60A",
  "Hazmat": "#FF9F0A",
  "Medic": "#FF2D55",
  "HEMS": "#A6F11C",
  "Patrol Car": "#1D4ED8",
  "Deputy": "#B45309",
  "K9 Unit": "#166534",
  "Sheriff": "#92400E",
  "Police Chief": "#CA8A04",
};

export const UNIT_SHORT = {
  "Engine": "ENG",
  "Ladder": "LAD",
  "Battalion Chief": "BC",
  "Tanker": "TNK",
  "Brush Truck": "BRU",
  "Rescue": "RES",
  "Hazmat": "HAZ",
  "Medic": "MED",
  "HEMS": "HEMS",
  "Patrol Car": "PATROL",
  "Deputy": "DEP",
  "K9 Unit": "K9",
  "Sheriff": "SHRF",
  "Police Chief": "CHIEF",
};

// Per-unit map-marker icon. Anything missing here defaults to engine_icon
// (e.g. Tanker / Brush Truck until custom artwork is provided).
export const UNIT_ICON = {
  "Engine": "/icons/engine_icon.png",
  "Ladder": "/icons/ladder_icon.png",
  "Battalion Chief": "/icons/battalionchief_icon.png",
  "Tanker": "/icons/tanker_icon.png",
  "Brush Truck": "/icons/brush_icon.png",
  "Rescue": "/icons/rescue_truck_icon.png",
  "Hazmat": "/icons/hazmat_icon.png",
  "Medic": "/icons/medic_icon.png",
  "HEMS": "/icons/hems_icon.png",
};
export const unitIcon = (type) => UNIT_ICON[type] || "/icons/engine_icon.png";

// ---- Hospital constants ----
export const HOSPITAL_COST = 1_000_000;
export const HOSPITAL_STARTING_BEDS = 10;
export const HOSPITAL_MAX_BAYS = 4;
export const HOSPITAL_BAY_COST = 50_000;
export const HOSPITAL_BAY_BUILD_SECONDS = 24 * 60 * 60; // 24 hours
// Cost of the Nth additional bed (beyond starting 10). Tiered: $20k base,
// +$2.5k each additional → 1st add'l=$20k, 2nd=$22.5k, 3rd=$25k, …
export const HOSPITAL_BED_BASE_COST = 20_000;
export const HOSPITAL_BED_STEP = 2_500;
export const bedCost = (currentBeds) => {
  const purchaseIndex = Math.max(0, currentBeds - HOSPITAL_STARTING_BEDS);
  return HOSPITAL_BED_BASE_COST + purchaseIndex * HOSPITAL_BED_STEP;
};

// Specialties a hospital can be upgraded with. Patients with a matching
// specialty tag get the full +$1000 reward when admitted; without, $500.
// Plain patients (no tag) award $600 anywhere.
export const HOSPITAL_SPECIALTIES = ["Trauma", "Cath Lab", "Stroke"];
export const HOSPITAL_SPECIALTY_COST = 150_000;

// Patient reward bands. Server mirrors these in routes/hospitals.py.
export const PATIENT_REWARD_STANDARD = 600;        // no specialty needed
export const PATIENT_REWARD_SPECIALTY_MATCH = 1000;// specialty needed AND hospital has it
export const PATIENT_REWARD_SPECIALTY_MISS = 500;  // specialty needed BUT hospital doesn't have it

// Patient mechanics
export const PATIENT_ASSESS_SECONDS = 30;          // max time per patient with 1 Medic
export const PATIENT_BED_TIME_STANDARD_MS = 30 * 60 * 1000; // 30 min
export const PATIENT_BED_TIME_SPECIALTY_MS = 60 * 60 * 1000; // 60 min

// ---- Spawn / pacing knobs ----
export const SPAWN_INTERVAL_MS = 60_000;
export const SPAWN_RADIUS_KM = 4.8280; // 3 miles
export const EXTRA_CONCURRENT_SLOTS = 1;

// Hard expiry for any call still pending — 6 hours wall-clock.
export const HARD_EXPIRY_SECONDS = 6 * 60 * 60; // 21,600 s

// ---- Routing (OSRM) ----
export const OSRM_BASE = "https://router.project-osrm.org";
export const OVER_LIMIT_MPH = 15;
export const TRAVEL_TIME_SCALE = 0.15;
export const TRAVEL_TIME_MIN_SEC = 4;

// ---- Emergency taxonomy ----
// `required_units` is a map of UnitType -> count needed on scene before the
// on-scene work timer starts. Reward & on_scene_duration scale with severity.
// `alternates` is an OPTIONAL list of alternative required-unit maps; the call
// is considered satisfied if EITHER `required_units` OR any entry in
// `alternates` is fully met on scene (OR-group semantics).
export const EMERGENCY_TYPES = {
  trash_fire: {
    label: "TRASH FIRE", short: "TRSH", icon: "/icons/fire_icon.png", color: "#FF9F0A",
    required_units: { Engine: 1 },
    on_scene_duration: 25, reward: 400, score_gain: 80, xp: 10,
  },
  debris_roadway: {
    label: "DEBRIS IN ROADWAY", short: "DBRS", icon: "/icons/rescue_icon.png", color: "#8E8E93",
    required_units: { Engine: 1 },
    alternates: [{ Ladder: 1 }, { Rescue: 1 }],
    on_scene_duration: 120, reward: 400, score_gain: 80, xp: 15,
    upgrades: [{ to: "car_accident", chance: 0.1 }],
  },
  utv_accident: {
    label: "UTV ACCIDENT", short: "UTV", icon: "/icons/ems_icon.png", color: "#0A84FF",
    required_units: { Engine: 1 },
    on_scene_duration: 25, reward: 600, score_gain: 100, xp: 15,
    patients: { min: 1, max: 1, transport_pct: 60, specialty: { Trauma: 50, "Cath Lab": 0, Stroke: 0 } },
  },
  vehicle_fire: {
    label: "VEHICLE FIRE", short: "VEH", icon: "/icons/fire_icon.png", color: "#FF6B30",
    required_units: { Engine: 1 },
    on_scene_duration: 45, reward: 800, score_gain: 120, xp: 20,
  },
  shed_fire: {
    label: "SHED FIRE", short: "SHED", icon: "/icons/fire_icon.png", color: "#FF6B30",
    required_units: { Engine: 1 },
    on_scene_duration: 55, reward: 1000, score_gain: 140, xp: 25,
  },
  car_accident: {
    label: "INJURY ACCIDENT", short: "MVA", icon: "/icons/rescue_icon.png", color: "#FF9F0A",
    required_units: { Engine: 2 },
    on_scene_duration: 50, reward: 1400, score_gain: 200, xp: 30,
    patients: { min: 1, max: 3, transport_pct: 50, specialty: { Trauma: 50, "Cath Lab": 0, Stroke: 0 } },
    upgrades: [{ to: "car_structure", chance: 0.05 }],
  },
  technical_rescue: {
    label: "TECHNICAL RESCUE", short: "RES", icon: "/icons/rescue_icon.png", color: "#FF9F0A",
    required_units: { Engine: 1, Rescue: 1 },
    on_scene_duration: 65, reward: 2000, score_gain: 260, xp: 50,
  },
  house_fire: {
    label: "HOUSE FIRE", short: "HOUSE", icon: "/icons/fire_icon.png", color: "#FF3B30",
    required_units: { Engine: 2, Ladder: 1 },
    on_scene_duration: 90, reward: 2600, score_gain: 320, xp: 65,
  },
  apartment_fire: {
    label: "APARTMENT FIRE", short: "APT", icon: "/icons/fire_icon.png", color: "#FF3B30",
    required_units: { Engine: 3, Ladder: 1, "Battalion Chief": 1 },
    on_scene_duration: 130, reward: 4200, score_gain: 500, xp: 100,
  },
  hazmat_1: {
    label: "HAZMAT SPILL", short: "HAZ", icon: "/icons/hazmat_logo_icon.png", color: "#BF5AF2",
    required_units: { Engine: 1, Hazmat: 1, "Battalion Chief": 1 },
    on_scene_duration: 160, reward: 5000, score_gain: 600, xp: 120,
    upgrades: [{ to: "hazmat_2", chance: 0.25 }],
  },
  industrial_fire: {
    label: "INDUSTRIAL FIRE", short: "INDU", icon: "/icons/fire_icon.png", color: "#BF1F12",
    required_units: { Engine: 3, Ladder: 1, "Battalion Chief": 1, Rescue: 1 },
    on_scene_duration: 200, reward: 8500, score_gain: 900, xp: 200,
  },
  wildfire: {
    label: "WILDFIRE", short: "WILD", icon: "/icons/fire_icon.png", color: "#BF1F12",
    required_units: { Engine: 1, "Brush Truck": 2, Tanker: 1, "Battalion Chief": 1 },
    on_scene_duration: 260, reward: 12000, score_gain: 1300, xp: 300,
  },
  small_grass_fire: {
    label: "SMALL GRASS FIRE", short: "GRASS", icon: "/icons/fire_icon.png", color: "#BF1F12",
    required_units: { "Brush Truck": 1 },
    on_scene_duration: 55, reward: 400, score_gain: 140, xp: 25,
    upgrades: [{ to: "med_grass_fire", chance: 0.25 }],
  },
  med_grass_fire: {
    label: "MEDIUM GRASS FIRE", short: "GRASS", icon: "/icons/fire_icon.png", color: "#BF1F12",
    required_units: { "Brush Truck": 2 },
    on_scene_duration: 50, reward: 1400, score_gain: 200, xp: 30,
    upgrades: [{ to: "large_grass_fire", chance: 0.1 }],
  },
  large_grass_fire: {
    label: "LARGE GRASS FIRE", short: "GRASS", icon: "/icons/fire_icon.png", color: "#BF1F12",
    required_units: { "Brush Truck": 3, Tanker: 1 },
    on_scene_duration: 120, reward: 2200, score_gain: 500, xp: 100,
    upgrades: [{ to: "wildfire", chance: 0.1 }],
  },
  gas_leak: {
    label: "GAS LEAK", short: "GASLK", icon: "/icons/fire_icon.png", color: "#BF1F12",
    required_units: { Engine: 1 },
    alternates: [{ Ladder: 1 }, { Rescue: 1 }],
    on_scene_duration: 120, reward: 400, score_gain: 80, xp: 15,
  },
  car_structure: {
    label: "INJURY ACCIDENT W/ STRUCTURE", short: "INJSTR", icon: "/icons/rescue_icon.png", color: "#FF9F0A",
    required_units: { Engine: 2, Rescue: 1, "Battalion Chief": 1 },
    on_scene_duration: 130, reward: 4000, score_gain: 500, xp: 100,
    patients: { min: 1, max: 3, transport_pct: 50, specialty: { Trauma: 50, "Cath Lab": 0, Stroke: 0 } },
  },
  cat_tree: {
    label: "ANIMAL IN TREE", short: "CAT", icon: "/icons/fire_icon.png", color: "#BF1F12",
    required_units: { Ladder: 1 },
    on_scene_duration: 25, reward: 250, score_gain: 80, xp: 10,
  },
  car_extrication: {
    label: "INJURY ACCIDENT W/ EXTRICATION", short: "INJEXT", icon: "/icons/rescue_icon.png", color: "#FF9F0A",
    required_units: { Engine: 2, Rescue: 1, "Battalion Chief": 1 },
    on_scene_duration: 130, reward: 4000, score_gain: 500, xp: 100,
    patients: { min: 1, max: 3, transport_pct: 50, specialty: { Trauma: 50, "Cath Lab": 0, Stroke: 0 } },
  },
  build_collapse: {
    label: "BUILDING COLLAPSE", short: "BUILD", icon: "/icons/fire_icon.png", color: "#BF1F12",
    required_units: { Engine: 4, Rescue: 2, "Battalion Chief": 1 },
    on_scene_duration: 260, reward: 12000, score_gain: 1300, xp: 300,
  },
  lift_assist: {
    label: "LIFT ASSIST", short: "LFT", icon: "/icons/ems_icon.png", color: "#0A84FF",
    required_units: { Engine: 1 },
    alternates: [{ Ladder: 1 }, { Rescue: 1 }],
    on_scene_duration: 25, reward: 150, score_gain: 80, xp: 10,
  },
  appliance_fire: {
    label: "APPLIANCE FIRE", short: "OVEN", icon: "/icons/fire_icon.png", color: "#BF1F12",
    required_units: { Engine: 2 },
    on_scene_duration: 50, reward: 1000, score_gain: 140, xp: 25,
  },
  sick_call: {
    label: "SICK CALL", short: "SICK", icon: "/icons/ems_icon.png", color: "#0A84FF",
    required_units: { Medic: 1 },
    on_scene_duration: 30, reward: 300, score_gain: 80, xp: 15,
    spawn_weight: 3.0,
    patients: { min: 1, max: 1, transport_pct: 30, specialty: { Trauma: 0, "Cath Lab": 0, Stroke: 0 } },
  },
  chest_pain: {
    label: "CHEST PAIN", short: "CHEST", icon: "/icons/ems_icon.png", color: "#FF6B6B",
    required_units: { Medic: 1 },
    on_scene_duration: 45, reward: 500, score_gain: 120, xp: 25,
    patients: { min: 1, max: 1, transport_pct: 80, specialty: { Trauma: 0, "Cath Lab": 25, Stroke: 0 } },
  },
  breathing_difficulty: {
    label: "BREATHING DIFFICULTY", short: "BREATH", icon: "/icons/ems_icon.png", color: "#FF9F0A",
    required_units: { Medic: 1 },
    on_scene_duration: 45, reward: 500, score_gain: 120, xp: 25,
    patients: { min: 1, max: 1, transport_pct: 80, specialty: { Trauma: 0, "Cath Lab": 0, Stroke: 0 } },
  },
  stroke: {
    label: "STROKE", short: "STRK", icon: "/icons/ems_icon.png", color: "#FF2D55",
    required_units: { Medic: 1 },
    on_scene_duration: 50, reward: 700, score_gain: 160, xp: 35,
    patients: { min: 1, max: 1, transport_pct: 70, specialty: { Trauma: 0, "Cath Lab": 0, Stroke: 30 } },
  },
  diabetic_problems: {
    label: "DIABETIC PROBLEMS", short: "DIAB", icon: "/icons/ems_icon.png", color: "#0A84FF",
    required_units: { Medic: 1 },
    on_scene_duration: 40, reward: 400, score_gain: 100, xp: 20,
    patients: { min: 1, max: 1, transport_pct: 50, specialty: { Trauma: 0, "Cath Lab": 0, Stroke: 0 } },
  },
  seizure: {
    label: "SEIZURE", short: "SEIZ", icon: "/icons/ems_icon.png", color: "#BF5AF2",
    required_units: { Medic: 1 },
    on_scene_duration: 45, reward: 500, score_gain: 120, xp: 25,
    patients: { min: 1, max: 1, transport_pct: 70, specialty: { Trauma: 0, "Cath Lab": 0, Stroke: 0 } },
  },
  cardiac_arrest: {
    label: "CARDIAC ARREST", short: "ARREST", icon: "/icons/ems_icon.png", color: "#FF3B30",
    required_units: { Medic: 1 },
    on_scene_duration: 120, reward: 900, score_gain: 220, xp: 50,
    spawn_weight: 0.3,
    assess_seconds: 120,
    patients: { min: 1, max: 1, transport_pct: 20, specialty: { Trauma: 0, "Cath Lab": 20, Stroke: 0 } },
  },
  powerline_down: {
    label: "POWERLINE DOWN", short: "PWRLN", icon: "/icons/rescue_icon.png", color: "#FF9F0A",
    required_units: { Engine: 1 },
    alternates: [{ Ladder: 1 }, { Rescue: 1 }],
    on_scene_duration: 30, reward: 450, score_gain: 80, xp: 20,
  },
  wildland_rescue: {
    label: "WILDLAND RESCUE", short: "RESC", icon: "/icons/rescue_icon.png", color: "#FF9F0A",
    required_units: { Engine: 1, Rescue: 1 },
    on_scene_duration: 65, reward: 750, score_gain: 80, xp: 40,
    patients: { min: 0, max: 1, transport_pct: 50, specialty: { Trauma: 50, "Cath Lab": 0, Stroke: 0 } },
  },
  odor_investigate: {
    label: "INVESTIGATE ODOR", short: "ODOR", icon: "/icons/fire_icon.png", color: "#BF1F12",
    required_units: { Engine: 1 },
    alternates: [{ Ladder: 1 }, { Rescue: 1 }, { "Battalion Chief": 1 }],
    on_scene_duration: 45, reward: 400, score_gain: 80, xp: 20,
  },
  elevator_assist: {
    label: "ELEVATOR ASSIST", short: "ASSIST", icon: "/icons/rescue_icon.png", color: "#FF9F0A",
    required_units: { Engine: 1, Rescue: 1 },
    on_scene_duration: 50, reward: 500, score_gain: 80, xp: 30,
  },
  angle_rescue: {
    label: "HIGH ANGLE RESCUE", short: "RESC", icon: "/icons/rescue_icon.png", color: "#FF9F0A",
    required_units: { Ladder: 1, Rescue: 1 },
    on_scene_duration: 60, reward: 600, score_gain: 80, xp: 30,
  },
  spill: {
    label: "SPILL", short: "SPILL", icon: "/icons/fire_icon.png", color: "#BF1F12",
    required_units: { Engine: 1 },
    alternates: [{ Hazmat: 1 }],
    on_scene_duration: 40, reward: 300, score_gain: 60, xp: 15,
  },
  tornado_strike_1: {
    label: "TORNADO STRIKE", short: "TOR", icon: "/icons/rescue_icon.png", color: "#FF9F0A",
    required_units: { Engine: 6, Rescue: 3, Ladder: 2, "Battalion Chief": 2 },
    on_scene_duration: 400, reward: 20000, score_gain: 1500, xp: 400,
    patients: { min: 1, max: 5, transport_pct: 60, specialty: { Trauma: 50, "Cath Lab": 0, Stroke: 0 } },
  },
  hazmat_2: {
    label: "HAZMAT SPILL (LARGE)", short: "HAZ", icon: "/icons/hazmat_logo_icon.png", color: "#BF5AF2",
    required_units: { Engine: 3, Hazmat: 2, "Battalion Chief": 1 },
    on_scene_duration: 320, reward: 16000, score_gain: 1500, xp: 400,
  },
  lightning_strike: {
    label: "LIGHTNING STRIKE", short: "LGHT", icon: "/icons/fire_icon.png", color: "#BF1F12",
    required_units: { Engine: 1 },
    alternates: [{ Ladder: 1 }, { Rescue: 1 }],
    on_scene_duration: 60, reward: 600, score_gain: 60, xp: 15,
    upgrades: [{ to: "house_fire", chance: 0.15 }],
  },
  oil_tank_fire: {
    label: "OIL TANK FIRE", short: "OIL", icon: "/icons/fire_icon.png", color: "#BF1F12",
    required_units: { Engine: 2, Hazmat: 1, "Battalion Chief": 1 },
    on_scene_duration: 320, reward: 12000, score_gain: 1200, xp: 350,
  },
};

// ---- Server hydration ----
// Convert a server Mission payload into the in-game EMERGENCY_TYPES shape.
// Server uses `on_scene_duration` / `reward` / etc. — they map 1:1 except
// `enabled` (filtered out by caller) and computed defaults.
const _missionToEmergency = (m) => {
  const out = {
    label: m.label,
    short: m.short || "",
    icon: m.icon || "/icons/fire_icon.png",
    color: m.color || "#FF9F0A",
    required_units: m.required_units || {},
    on_scene_duration: Number(m.on_scene_duration) || 0,
    reward: Number(m.reward) || 0,
    score_gain: Number(m.score_gain) || 0,
    xp: Number(m.xp) || 0,
  };
  if (Array.isArray(m.alternates) && m.alternates.length) out.alternates = m.alternates;
  if (Array.isArray(m.upgrades) && m.upgrades.length) out.upgrades = m.upgrades;
  if (typeof m.spawn_weight === "number") out.spawn_weight = m.spawn_weight;
  if (typeof m.assess_seconds === "number") out.assess_seconds = m.assess_seconds;
  if (typeof m.service_type === "string") out.service_type = m.service_type;
  if (typeof m.arrest_chance_pct === "number") out.arrest_chance_pct = m.arrest_chance_pct;
  if (typeof m.event_only === "boolean") out.event_only = m.event_only;
  if (m.patients && typeof m.patients === "object") {
    // Preserve all known patient fields when hydrating from the server. The
    // hems_required_pct field is what gates HEMS-only patients downstream.
    out.patients = {
      min: Number(m.patients.min) || 0,
      max: Number(m.patients.max) || 0,
      transport_pct: Number(m.patients.transport_pct) || 0,
      specialty: m.patients.specialty || {},
      hems_required_pct: Number(m.patients.hems_required_pct) || 0,
    };
  }
  return out;
};

/**
 * Replace the contents of EMERGENCY_TYPES with the server-provided catalog.
 * MUTATES the existing object so every existing import keeps working (live
 * binding semantics) — engine + UI lookups continue to read the latest data.
 * Disabled missions are dropped.
 *
 * Called once at app startup from Game.jsx BEFORE the engine begins ticking.
 * Safe to call again after a dev edit to re-hydrate without a page reload.
 */
export function hydrateEmergencyTypes(missions) {
  if (!Array.isArray(missions)) return;
  // Wipe existing keys (in-place to preserve reference identity).
  for (const k of Object.keys(EMERGENCY_TYPES)) delete EMERGENCY_TYPES[k];
  for (const m of missions) {
    if (!m || !m.id || m.enabled === false) continue;
    EMERGENCY_TYPES[m.id] = _missionToEmergency(m);
  }
}

// ---- Helpers ----
export const distanceKm = (a, b) => {
  const toRad = (x) => (x * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
};

// Sum of each unit type the player owns across all stations
export const totalUnitsByType = (stations) => {
  const counts = {};
  UNIT_TYPES.forEach((u) => (counts[u] = 0));
  stations.forEach((s) => {
    (s.trucks || []).forEach((t) => {
      if (counts[t.type] !== undefined) counts[t.type] += 1;
    });
  });
  return counts;
};

// Player has enough of each required unit type owned (not necessarily available)
export const playerCanHandle = (stations, requiredUnits) => {
  const have = totalUnitsByType(stations);
  return Object.entries(requiredUnits).every(([type, n]) => (have[type] || 0) >= n);
};

// All requirement options for a call type: the primary required_units PLUS any
// `alternates`. Returns a non-empty array. Used by OR-group spawn gating &
// "is satisfied?" checks throughout the engine.
export const allRequirementOptions = (t) => {
  if (!t) return [{}];
  const opts = [t.required_units || {}];
  if (Array.isArray(t.alternates)) opts.push(...t.alternates);
  return opts;
};

// Player can spawn this call type if they own enough units to satisfy AT LEAST
// ONE of its requirement options.
export const playerCanHandleType = (stations, t) =>
  allRequirementOptions(t).some((opt) => playerCanHandle(stations, opt));

// Roll the upgrades list (if any) and return the FIRST target id whose dice
// roll succeeded — null if none. Each entry is independent: `{to, chance}`
// where chance is a 0..1 probability. Order matters: earlier entries win.
export const rollCallUpgrade = (t) => {
  if (!t || !Array.isArray(t.upgrades) || t.upgrades.length === 0) return null;
  for (const u of t.upgrades) {
    if (!u || !u.to) continue;
    const p = Math.max(0, Math.min(1, Number(u.chance) || 0));
    if (Math.random() < p) return u.to;
  }
  return null;
};

export const eligibleEmergencyKeys = (stations) =>
  Object.entries(EMERGENCY_TYPES)
    .filter(([, t]) => playerCanHandleType(stations, t))
    .map(([k]) => k);

// Active-event mission whitelist. Populated from the /api/events/active
// response on load. `event_only` missions stay OUT of the spawn pool
// unless their id appears here.
export const ACTIVE_EVENT_MISSION_IDS = new Set();
export function setActiveEventMissionIds(ids) {
  ACTIVE_EVENT_MISSION_IDS.clear();
  (ids || []).forEach((id) => ACTIVE_EVENT_MISSION_IDS.add(id));
}

// Weighted random pick. Each EMERGENCY_TYPES entry may carry an optional
// `spawn_weight` (default 1.0). The bigger the weight, the more often the
// call type is rolled. A weight of 0 makes the call un-spawnable even if
// the player is otherwise eligible (handy for retired calls).
export const pickEmergencyType = (stations) => {
  const rawKeys = eligibleEmergencyKeys(stations);
  const keys = rawKeys.filter((k) => {
    const t = EMERGENCY_TYPES[k];
    if (!t) return false;
    // Event-only missions only spawn while their id is in the active
    // event whitelist. Everything else stays in the pool as normal.
    if (t.event_only) return ACTIVE_EVENT_MISSION_IDS.has(k);
    return true;
  });
  if (keys.length === 0) return null;
  const weights = keys.map((k) => {
    const w = Number(EMERGENCY_TYPES[k]?.spawn_weight);
    return Number.isFinite(w) && w >= 0 ? w : 1;
  });
  const total = weights.reduce((a, b) => a + b, 0);
  if (total <= 0) return null;
  let roll = Math.random() * total;
  for (let i = 0; i < keys.length; i++) {
    roll -= weights[i];
    if (roll < 0) return keys[i];
  }
  return keys[keys.length - 1];
};

export const stationIconSizeForZoom = (zoom) => {
  if (zoom >= 10) return 40;
  if (zoom <= 3) return 14;
  return Math.round(14 + ((zoom - 3) / 7) * 26);
};

// Count Medics the player owns across stations AND hospitals. Used to
// cap patient generation so we never spawn more patients on one call than
// the player could possibly transport (Medic or HEMS).
export const countOwnedMedics = (stations, hospitals) => {
  let n = 0;
  const isTransport = (t) => t === "Medic" || t === "HEMS";
  (stations || []).forEach((s) => (s.trucks || []).forEach((t) => { if (isTransport(t.type)) n += 1; }));
  (hospitals || []).forEach((h) => (h.trucks || []).forEach((t) => { if (isTransport(t.type)) n += 1; }));
  return n;
};

// ---- Patient generation ----
// Called when the FIRST unit arrives on scene of a call. If the emergency
// type has a `patients` config block we roll a count, then per-patient roll
// whether they need transport and (if so) whether they have a specialty tag.
// Returns an array of patient objects (possibly empty).
//
// `maxOwnedMedics` (optional) caps the count so the player can never face
// more patients in a single incident than they have Medic apparatus to
// transport. Pass undefined/null to skip the cap.
//
// Count distribution is WEIGHTED so the lower end of the [min, max] range
// dominates. Using `0.5^index` decay, with min=1, max=3 the result is
// roughly 57% / 29% / 14% (1 / 2 / 3 patients). This makes Injury Accident
// (and the w/ Structure / w/ Extrication variants) usually a single-patient
// scene, with multi-patient cases progressively rarer.
// ---- Patient name generation ----
// Fake first names + last-initial drawn at random when a patient is spawned.
// Kept short and family-friendly. Mix of common American + international
// names so the rotation feels varied without becoming gimmicky.
const PATIENT_FIRST_NAMES = [
  "Mark", "Sadie", "James", "Maria", "Tyler", "Olivia", "Ethan", "Aisha",
  "Connor", "Priya", "Jamal", "Hannah", "Diego", "Emma", "Liam", "Yuki",
  "Noah", "Zoe", "Ava", "Wyatt", "Lucia", "Brody", "Mei", "Ellis",
  "Cole", "Riley", "Bria", "Owen", "Layla", "Mateo", "Sofia", "Kai",
  "Nina", "Jonas", "Quinn", "Ezra", "Maya", "Theo", "Iris", "Otis",
  "Sasha", "Eli", "Nora", "Ravi", "Cora", "Beck", "Ada", "Hugo",
  "Sage", "Reid", "Lena", "Ari", "Tess", "Finn", "Vera", "Dax",
];
const LAST_INITIALS = "ABCDEFGHIJKLMNOPRSTVW".split("");
const _randFrom = (arr) => arr[Math.floor(Math.random() * arr.length)];
// "Mark F.", "Sadie B." style. Used for the patient list shown on a call.
export const generatePatientName = () =>
  `${_randFrom(PATIENT_FIRST_NAMES)} ${_randFrom(LAST_INITIALS)}.`;

export const generatePatientsForCall = (emergencyType, callId, maxOwnedMedics = null) => {
  const cfg = emergencyType?.patients;
  if (!cfg) return [];
  const min = Math.max(0, Number(cfg.min) || 0);
  const rawMax = Math.max(min, Number(cfg.max) || 0);
  if (rawMax === 0) return [];
  // Apply the medic cap. If player owns fewer medics than `min`, the
  // effective ceiling drops to whatever medics they have (possibly 0).
  const cap = (maxOwnedMedics == null) ? rawMax : Math.min(rawMax, Math.max(0, Number(maxOwnedMedics) || 0));
  if (cap === 0) return [];
  const effMin = Math.min(min, cap);
  const span = cap - effMin + 1;
  // Weighted pick — decaying 0.5^i biases toward `effMin`.
  const weights = [];
  let total = 0;
  for (let i = 0; i < span; i++) {
    const w = Math.pow(0.5, i);
    weights.push(w);
    total += w;
  }
  let roll = Math.random() * total;
  let pick = 0;
  for (let i = 0; i < span; i++) {
    roll -= weights[i];
    if (roll < 0) { pick = i; break; }
  }
  const count = effMin + pick;
  const transportPct = Math.max(0, Math.min(100, Number(cfg.transport_pct) || 0));
  const specCfg = cfg.specialty || {};
  // Independent roll AFTER `needsTransport`. If true the patient is too
  // critical for a ground ambulance — only a HEMS helicopter can transport
  // them. Surfaced post-assessment as `requires_hems: true`.
  const hemsReqPct = Math.max(0, Math.min(100, Number(cfg.hems_required_pct) || 0));
  const patients = [];
  for (let i = 0; i < count; i++) {
    const needsTransport = Math.random() * 100 < transportPct;
    let specialty = null;
    let requires_hems = false;
    if (needsTransport) {
      // Roll each specialty independently — first hit wins (mutually
      // exclusive tag). Order: Trauma, Cath Lab, Stroke.
      for (const sp of HOSPITAL_SPECIALTIES) {
        const pct = Math.max(0, Math.min(100, Number(specCfg[sp]) || 0));
        if (Math.random() * 100 < pct) { specialty = sp; break; }
      }
      requires_hems = Math.random() * 100 < hemsReqPct;
    }
    patients.push({
      id: `p_${callId}_${i}_${Math.random().toString(36).slice(2, 7)}`,
      name: generatePatientName(),  // e.g. "Mark F." — shown in CallDialog
      assessed: false,
      assessProgress: 0,            // 0..1
      assessStartedAt: null,
      needsTransport,                // truth — revealed AFTER assessment
      specialty,                     // truth — revealed AFTER assessment
      requires_hems,                 // truth — only HEMS can transport when true
      transportStatus: "none",       // none|queued|in_transit|delivered|cancelled|refused
      destinationHospitalId: null,
      reservationId: null,
      reward: 0,                     // filled in when delivered
    });
  }
  return patients;
};

// ---- Patient reward calculation ----
// `patient.specialty` is the tag they need (or null = no specialty).
// `hospital.specialties` is the list the destination hospital has.
export const patientReward = (patient, hospital) => {
  if (!patient?.needsTransport) return 0;
  if (!patient.specialty) return PATIENT_REWARD_STANDARD;
  const has = Array.isArray(hospital?.specialties) && hospital.specialties.includes(patient.specialty);
  return has ? PATIENT_REWARD_SPECIALTY_MATCH : PATIENT_REWARD_SPECIALTY_MISS;
};

// Total free bed count for a hospital (beds minus reservations minus active occupancies).
export const hospitalFreeBeds = (h) => {
  if (!h) return 0;
  const total = Number(h.beds) || 0;
  const now = Date.now();
  const occupied = (h.occupied_beds || []).filter((o) => {
    const until = typeof o.freed_at === "string" ? Date.parse(o.freed_at) : Number(o.freed_at) || 0;
    return until > now;
  }).length;
  const reserved = (h.reservations || []).filter((r) => {
    const exp = typeof r.expires_at === "string" ? Date.parse(r.expires_at) : Number(r.expires_at) || 0;
    return exp > now;
  }).length;
  return Math.max(0, total - occupied - reserved);
};

// Has the player (across own + alliance-shared hospitals) at least one hospital?
// Drives Medic-purchase gating.
export const playerHasAnyHospital = (hospitals, allianceHospitals) =>
  (hospitals && hospitals.length > 0) ||
  (allianceHospitals && allianceHospitals.length > 0);
